import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { CompanyResource } from '@/types/resources';
import { api } from '@/utils/api';

import CompanyFormModal from './CompanyFormModal';

const MAX_ITEMS_DISPLAYED = 6;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.purple};
  flex-shrink: 0;
`;

const ItemsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ShowMoreButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gray};
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 0;
  margin-top: 4px;
  transition: color ease-out 0.2s;
  text-align: left;
  &:hover {
    color: ${({ theme }) => theme.colors.darkGray};
  }
`;

interface RelatedItem {
  id: number;
  name: string;
}

const RelatedList: FC<{ items: RelatedItem[] }> = ({ items }) => {
  const [showAll, setShowAll] = useState(false);

  if (!items.length) return <>-</>;

  const displayed = showAll ? items : items.slice(0, MAX_ITEMS_DISPLAYED);
  const hiddenCount = items.length - MAX_ITEMS_DISPLAYED;

  return (
    <ItemsWrap>
      {displayed.map((item) => (
        <Item key={item.id}>
          <Dot />
          {item.name}
        </Item>
      ))}
      {hiddenCount > 0 && (
        <ShowMoreButton onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Show less' : `Show more (${hiddenCount})`}
        </ShowMoreButton>
      )}
    </ItemsWrap>
  );
};

const DetailLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// IBANs are long and the table is fixed-width, so let them wrap rather than
// blow the column out.
const MutedDetail = styled.span`
  color: ${({ theme }) => theme.colors.gray};
  word-break: break-all;
`;

const CompanyDetails: FC<{ company: CompanyResource }> = ({ company }) => {
  const {
    registration_code: registrationCode,
    vat_code: vatCode,
    address,
    bank_account: bankAccount,
  } = company;

  // Same order and joining as the buyer block printed on invoices.
  const locality = [company.city, company.country, company.post_code]
    .filter((part) => part?.trim())
    .join(', ');

  if (!registrationCode && !vatCode && !address && !locality && !bankAccount) {
    return <>-</>;
  }

  return (
    <DetailLines>
      {!!registrationCode && (
        <span>
          <MutedDetail>Reg. </MutedDetail>
          {registrationCode}
        </span>
      )}
      {!!vatCode && (
        <span>
          <MutedDetail>VAT </MutedDetail>
          {vatCode}
        </span>
      )}
      {!!address && <span>{address}</span>}
      {!!locality && <span>{locality}</span>}
      {!!bankAccount && (
        <>
          <span>{bankAccount.bank_name}</span>
          <MutedDetail>{bankAccount.iban}</MutedDetail>
          <MutedDetail>{bankAccount.bic}</MutedDetail>
        </>
      )}
    </DetailLines>
  );
};

interface Props {
  company: CompanyResource;
  departments: RelatedItem[];
  subunits: RelatedItem[];
  showFinanceDetails: boolean;
}

const CompanyTableRow: FC<Props> = ({
  company,
  departments,
  subunits,
  showFinanceDetails,
}) => {
  const [showEditModal, setShowEditModal] = useState(false);

  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this company?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(`/v1/companies/${company.id}`);

      await queryClient.invalidateQueries(['companies'], {
        exact: false,
      });

      alerts.success('Company was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <>
      <tr>
        <Td>{company.name}</Td>
        <Td>{company.short ?? '-'}</Td>
        {showFinanceDetails && (
          <Td>
            <CompanyDetails company={company} />
          </Td>
        )}
        <Td>
          <RelatedList items={departments} />
        </Td>
        <Td>
          <RelatedList items={subunits} />
        </Td>
        <Td>{company.hide_from_dropdowns ? 'Yes' : 'No'}</Td>
        <Td $actionsCol>
          <ActionsWrap>
            <ActionsDropdown>
              <ActionsDropdownButton
                as="button"
                onClick={() => setShowEditModal(true)}
              >
                <span>
                  <PencilIcon />
                </span>
                Edit
              </ActionsDropdownButton>
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </ActionsDropdown>
          </ActionsWrap>
        </Td>
      </tr>
      <AnimatePresence>
        {showEditModal && (
          <CompanyFormModal
            company={company}
            onClose={() => {
              setShowEditModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default CompanyTableRow;
